import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Snackbar from "@mui/material/Snackbar";
import Tooltip from "@mui/material/Tooltip";
import { CircleAlert, CircleCheck, CircleOff, ExternalLink, Home, Info, LogIn, LogOut, Menu, MessageCircle, Moon, PackageOpen, Plus, Settings, ShieldCheck, Sparkles, Store, Sun, UserPlus, UserRound, UsersRound, X } from "lucide-react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { useThemeMode } from "../context/ThemeContext";
import { initials } from "../lib/format";
import { openMallHome } from "../lib/external-links";
import type { ToastTone } from "../lib/toast";
import { Button } from "./ui/button";
import { SessionReplacedDialog } from "./SessionReplacedDialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";

function Brand({ compact = false, name, avatarUrl }: { compact?: boolean; name: string; avatarUrl: string | null }) {
  return (
    <NavLink className="brand" to="/square" aria-label={`返回${name}`}>
      <Avatar className={`brand-mark ${avatarUrl ? "has-image" : ""}`} src={avatarUrl ?? undefined} aria-hidden="true">
        {avatarUrl ? null : <Sparkles />}
      </Avatar>
      <span><strong>{name}</strong>{compact ? null : <small>SHARE MOMENTS</small>}</span>
    </NavLink>
  );
}

function ToastHost() {
  const [toast, setToast] = useState<{ message: string; tone: ToastTone } | null>(null);
  useEffect(() => {
    let timer: number | undefined;
    const show = (event: Event) => {
      const detail = (event as CustomEvent<{ message: string; tone: ToastTone }>).detail;
      setToast(detail);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setToast(null), 4200);
    };
    window.addEventListener("share-moments:toast", show);
    return () => {
      window.removeEventListener("share-moments:toast", show);
      window.clearTimeout(timer);
    };
  }, []);
  const tone = toast?.tone ?? "neutral";
  const severity = tone === "danger" ? "error" : tone === "success" ? "success" : "info";
  const icon = tone === "danger" ? <CircleAlert aria-hidden="true" /> : tone === "success" ? <CircleCheck aria-hidden="true" /> : <Info aria-hidden="true" />;
  return <Snackbar className="global-toast-snackbar" open={Boolean(toast)} anchorOrigin={{ vertical: "top", horizontal: "center" }}><Alert className={`toast toast-${tone}`} severity={severity} icon={icon} role="status"><span className="toast-message">{toast?.message}</span></Alert></Snackbar>;
}

export function AppShell() {
  const { config, user, unread, loginPrompt, dismissLoginPrompt, sessionReplaced, acknowledgeSessionReplaced, logout } = useApp();
  const { mode, toggleTheme } = useThemeMode();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const canPublish = Boolean(user && user.status !== "publish_restricted");
  const messageUnread = unread.chat + unread.activity;
  const disclaimer = config?.disclaimerMode === "compact" ? config.disclaimerCompact : config?.disclaimerFull;
  const site = config?.site ?? { name: "共享朋友圈", tagline: "分享游戏日常，认识同好玩家", avatarUrl: null };
  const closeMobileMenu = () => setMobileMenuOpen(false);

  const openMall = () => {
    if (config?.mallConfigured) openMallHome(config.mallBaseUrl);
    closeMobileMenu();
  };

  const continueToLogin = () => {
    if (!loginPrompt) return;
    const nextPath = loginPrompt.nextPath;
    dismissLoginPrompt();
    navigate(`/login?next=${encodeURIComponent(nextPath)}`);
  };

  const continueAfterSessionReplaced = () => {
    acknowledgeSessionReplaced();
    closeMobileMenu();
    navigate("/login", { replace: true });
  };

  return (
    <div className="app-root">
      <a className="skip-link" href="#main-content">跳到主要内容</a>
      <header className="site-header">
        <div className="header-inner">
          <Brand compact name={site.name} avatarUrl={site.avatarUrl} />
          <nav className="desktop-nav" aria-label="主导航">
            <NavLink to="/square">朋友圈</NavLink>
            <NavLink to="/groups" aria-label={`官方群，${unread.groups} 条未读`}><Badge className="t-notification-badge" badgeContent={unread.groups} max={99} invisible={unread.groups === 0} color="primary"><span>官方群</span></Badge></NavLink>
            {canPublish ? <NavLink to="/warehouse">内容仓库</NavLink> : null}
          </nav>
          <div className="header-actions">
            {user ? (
              <>
                {canPublish ? <Button asChild size="sm" className="header-publish"><NavLink to="/post/new"><Plus aria-hidden="true" />发布动态</NavLink></Button> : null}
                <Tooltip title={messageUnread > 0 ? `${messageUnread} 条未读消息` : "消息"}>
                  <IconButton className="header-message" component={NavLink} to="/messages" aria-label={`消息，${messageUnread} 条未读`}>
                    <Badge className="t-notification-badge" badgeContent={messageUnread} max={99} invisible={messageUnread === 0} color="primary"><MessageCircle aria-hidden="true" /></Badge>
                  </IconButton>
                </Tooltip>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <IconButton className="user-menu-trigger" aria-label="打开账户菜单">
                      <Avatar src={user.avatarUrl ?? undefined}>{initials(user.nickname)}</Avatar>
                    </IconButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="shell-menu" align="end">
                    <DropdownMenuLabel><strong>{user.nickname}</strong><small>{user.account}</small></DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup>
                      <DropdownMenuItem onSelect={() => navigate(`/seller/${user.id}`)}><UserRound aria-hidden="true" />个人主页</DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => navigate("/account")}><Settings aria-hidden="true" />账户设置</DropdownMenuItem>
                      {canPublish ? <DropdownMenuItem onSelect={() => navigate("/warehouse")}><PackageOpen aria-hidden="true" />内容仓库</DropdownMenuItem> : null}
                      {user.role === "admin" ? <DropdownMenuItem onSelect={() => navigate("/admin")}><ShieldCheck aria-hidden="true" />管理后台</DropdownMenuItem> : null}
                    </DropdownMenuGroup>
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup>
                      <DropdownMenuItem disabled={!config?.mallConfigured} onSelect={() => config?.mallConfigured && openMallHome(config.mallBaseUrl)}><Store aria-hidden="true" />{config?.mallConfigured ? config.mallEntryLabel : "商城未配置"}</DropdownMenuItem>
                      <DropdownMenuItem onSelect={toggleTheme}>{mode === "dark" ? <Sun aria-hidden="true" /> : <Moon aria-hidden="true" />}{mode === "dark" ? "切换浅色模式" : "切换深色模式"}</DropdownMenuItem>
                    </DropdownMenuGroup>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="is-danger" onSelect={() => { logout(); navigate("/square"); }}><LogOut aria-hidden="true" />退出登录</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Tooltip title={mode === "dark" ? "切换浅色模式" : "切换深色模式"}>
                  <IconButton className="guest-theme-toggle" type="button" aria-label={mode === "dark" ? "切换浅色模式" : "切换深色模式"} onClick={toggleTheme}>{mode === "dark" ? <Sun /> : <Moon />}</IconButton>
                </Tooltip>
                <Button asChild size="sm" variant="outline" className="header-register"><NavLink to="/register">注册</NavLink></Button>
                <Button asChild size="sm" className="header-login"><NavLink to="/login"><LogIn aria-hidden="true" />登录</NavLink></Button>
              </>
            )}
            <Tooltip title="打开导航">
              <IconButton className="mobile-menu-trigger" type="button" aria-label="打开导航菜单" onClick={() => setMobileMenuOpen(true)}><Menu aria-hidden="true" /></IconButton>
            </Tooltip>
          </div>
        </div>
      </header>

      <main id="main-content"><Outlet /></main>

      <nav className="mobile-bottom-nav" aria-label="移动端主导航">
        <NavLink to="/square"><Home aria-hidden="true" /><span>广场</span></NavLink>
        <NavLink to="/groups" aria-label={`社群，${unread.groups} 条未读`}><Badge className="t-notification-badge" badgeContent={unread.groups} max={99} invisible={unread.groups === 0} color="primary"><UsersRound aria-hidden="true" /></Badge><span>社群</span></NavLink>
        {canPublish ? <NavLink className="mobile-bottom-publish" to="/post/new" aria-label="发布动态"><Plus aria-hidden="true" /><span>发布</span></NavLink> : user ? <span className="mobile-bottom-publish is-disabled" aria-disabled="true" title="发布权限已关闭"><CircleOff aria-hidden="true" /><span>发布关闭</span></span> : <NavLink className="mobile-bottom-publish" to="/login?next=%2Fpost%2Fnew" aria-label="登录后发布动态"><Plus aria-hidden="true" /><span>发布</span></NavLink>}
        {user ? <NavLink to="/messages" aria-label={`消息，${messageUnread} 条未读`}><Badge className="t-notification-badge" badgeContent={messageUnread} max={99} invisible={messageUnread === 0} color="primary"><MessageCircle aria-hidden="true" /></Badge><span>消息</span></NavLink> : <NavLink to="/login"><MessageCircle aria-hidden="true" /><span>消息</span></NavLink>}
        {user ? <NavLink to={`/seller/${user.id}`} aria-label="我的主页"><Avatar src={user.avatarUrl ?? undefined}>{initials(user.nickname)}</Avatar><span>我的</span></NavLink> : <NavLink to="/login"><LogIn aria-hidden="true" /><span>登录</span></NavLink>}
      </nav>

      <footer className="site-footer">
        <div className="page-shell footer-grid">
          <div className="footer-brand"><Brand name={site.name} avatarUrl={site.avatarUrl} /><small>{site.tagline}</small></div>
          <div className="disclaimer"><strong>风险与免责声明</strong><p>{disclaimer ?? "正在加载平台声明..."}</p></div>
          <nav className="footer-links" aria-label="法律与帮助"><NavLink to="/legal/terms">用户协议</NavLink><NavLink to="/legal/privacy">隐私政策</NavLink><NavLink to="/legal/community">社区规范</NavLink><NavLink to="/legal/report">举报入口</NavLink></nav>
        </div>
      </footer>

      <Drawer anchor="right" open={mobileMenuOpen} onClose={closeMobileMenu} slotProps={{ paper: { className: "mobile-menu-paper" } }}>
        <div className="mobile-menu-header">
          <Brand compact name={site.name} avatarUrl={site.avatarUrl} />
          <IconButton type="button" aria-label="关闭导航菜单" onClick={closeMobileMenu}><X aria-hidden="true" /></IconButton>
        </div>
        {user ? <div className="mobile-account-card"><Avatar src={user.avatarUrl ?? undefined}>{initials(user.nickname)}</Avatar><span><strong>{user.nickname}</strong><small>@{user.account}</small></span></div> : <div className="mobile-menu-welcome"><span>欢迎来到社区</span><strong>发现此刻正在发生的游戏故事</strong></div>}
        <List className="mobile-menu-list" aria-label="移动端主导航">
          <ListItemButton component={NavLink} to="/square" onClick={closeMobileMenu}><ListItemIcon><Home /></ListItemIcon><ListItemText primary="朋友圈" secondary="浏览公开动态" /></ListItemButton>
          <ListItemButton component={NavLink} to="/groups" onClick={closeMobileMenu}><ListItemIcon><Badge className="t-notification-badge" badgeContent={unread.groups} max={99} invisible={unread.groups === 0} color="primary"><UsersRound /></Badge></ListItemIcon><ListItemText primary="官方社群" secondary={unread.groups ? `${unread.groups} 条群消息未读` : "和同好玩家交流"} /></ListItemButton>
          {user ? <ListItemButton component={NavLink} to="/messages" onClick={closeMobileMenu}><ListItemIcon><Badge className="t-notification-badge" badgeContent={messageUnread} max={99} invisible={messageUnread === 0} color="primary"><MessageCircle /></Badge></ListItemIcon><ListItemText primary="消息" secondary={messageUnread ? `${messageUnread} 条未读` : "私信与互动提醒"} /></ListItemButton> : null}
          {user ? <ListItemButton component={NavLink} to={`/seller/${user.id}`} onClick={closeMobileMenu}><ListItemIcon><UserRound /></ListItemIcon><ListItemText primary="个人主页" secondary="查看公开资料与动态" /></ListItemButton> : null}
          {user ? <ListItemButton component={NavLink} to="/account" onClick={closeMobileMenu}><ListItemIcon><Settings /></ListItemIcon><ListItemText primary="账户设置" secondary="资料与安全" /></ListItemButton> : null}
          {canPublish ? <ListItemButton component={NavLink} to="/warehouse" onClick={closeMobileMenu}><ListItemIcon><PackageOpen /></ListItemIcon><ListItemText primary="内容仓库" secondary="上下架与复用素材" /></ListItemButton> : null}
          {user?.role === "admin" ? <ListItemButton component={NavLink} to="/admin" onClick={closeMobileMenu}><ListItemIcon><ShieldCheck /></ListItemIcon><ListItemText primary="管理后台" secondary="运营与内容治理" /></ListItemButton> : null}
        </List>
        <Divider />
        <List className="mobile-menu-list mobile-menu-secondary">
          <ListItemButton onClick={toggleTheme}><ListItemIcon>{mode === "dark" ? <Sun /> : <Moon />}</ListItemIcon><ListItemText primary={mode === "dark" ? "浅色模式" : "深色模式"} /></ListItemButton>
          <ListItemButton disabled={!config?.mallConfigured} onClick={openMall}><ListItemIcon><Store /></ListItemIcon><ListItemText primary={config?.mallConfigured ? config.mallEntryLabel : "商城未配置"} /><ExternalLink className="menu-trailing-icon" /></ListItemButton>
        </List>
        <div className="mobile-menu-actions">
          {canPublish ? <Button asChild className="button-full"><NavLink to="/post/new" onClick={closeMobileMenu}><Plus />发布动态</NavLink></Button> : null}
          {user ? <Button variant="outline" className="button-full" type="button" onClick={() => { logout(); closeMobileMenu(); navigate("/square"); }}><LogOut />退出登录</Button> : <><Button asChild className="button-full"><NavLink to="/login" onClick={closeMobileMenu}><LogIn />登录</NavLink></Button><Button asChild variant="outline" className="button-full"><NavLink to="/register" onClick={closeMobileMenu}><UserPlus />创建账号</NavLink></Button></>}
        </div>
      </Drawer>
      <Dialog className="auth-required-dialog" open={Boolean(loginPrompt)} onClose={dismissLoginPrompt} aria-labelledby="auth-required-title" aria-describedby="auth-required-description">
        <DialogContent>
          <span className="auth-required-icon" aria-hidden="true"><LogIn /></span>
          <DialogTitle id="auth-required-title">登录后继续</DialogTitle>
          <DialogContentText id="auth-required-description">{loginPrompt?.message}</DialogContentText>
          <p className="auth-required-note">登录完成后会返回刚才的操作位置。</p>
          <DialogActions>
            <Button variant="outline" type="button" onClick={dismissLoginPrompt}>暂不</Button>
            <Button type="button" onClick={continueToLogin}><LogIn aria-hidden="true" />去登录</Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
      <SessionReplacedDialog open={sessionReplaced} onContinue={continueAfterSessionReplaced} />
      <ToastHost />
    </div>
  );
}
